import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  TrendingDown,
  Loader2,
  AlertTriangle,
  ArrowRight,
  FileDown,
  RotateCcw,
  CheckCircle2,
  X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Análisis del vademécum del lab vs estudio en curso.
 *
 * Iter C: incluye vista previa molécula-a-CN antes de aplicar, con export a
 * Excel y botón "Retornar al modelo anterior" (persistente, sin cuenta atrás).
 */
export default function LabAnalysisPanel({ laboratorio }) {
  const { user } = useAuth() || {};
  const isAdmin = user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("agreement");
  const [onlyBuying, setOnlyBuying] = useState(false);

  // Preview state
  const [preview, setPreview] = useState(null); // {target_lab, rows, totals}
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedCns, setSelectedCns] = useState(new Set());
  const [applying, setApplying] = useState(false);

  // Snapshot state
  const [snapshot, setSnapshot] = useState(null); // {exists, created_at, n_entries}
  const [reverting, setReverting] = useState(false);

  const loadAnalysis = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get(`/supplier-agreements/${encodeURIComponent(laboratorio)}/molecule-analysis`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "No se pudo cargar el análisis"))
      .finally(() => setLoading(false));
  }, [laboratorio]);

  const loadSnapshot = useCallback(() => {
    api
      .get(`/lab-analysis/${encodeURIComponent(laboratorio)}/transfer/snapshot`)
      .then((r) => setSnapshot(r.data?.exists ? r.data : null))
      .catch(() => setSnapshot(null));
  }, [laboratorio]);

  useEffect(() => {
    loadAnalysis();
    loadSnapshot();
  }, [loadAnalysis, loadSnapshot]);

  const closePreview = () => {
    setPreview(null);
    setSelectedCns(new Set());
  };

  const loadPreview = async (pairs) => {
    if (!pairs?.length) {
      toast.error("Selecciona al menos una molécula");
      return;
    }
    setPreviewLoading(true);
    try {
      const { data: pv } = await api.post(
        `/lab-analysis/${encodeURIComponent(laboratorio)}/transfer/preview`,
        { gh_current_lab_pairs: pairs, disp_id: data?.disp_id || "" },
      );
      setPreview(pv);
      setSelectedCns(new Set((pv.rows || []).map((r) => r.cn)));
      // scroll to preview
      setTimeout(() => {
        const el = document.querySelector('[data-testid="lab-transfer-preview"]');
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar la vista previa");
    } finally {
      setPreviewLoading(false);
    }
  };

  const exportPreviewExcel = async () => {
    if (!preview?.rows?.length) return;
    try {
      const pairs = preview.rows.map((r) => ({ gh: r.gh, current_lab: r.current_lab }));
      const uniqPairs = Array.from(
        new Map(pairs.map((p) => [`${p.gh}||${p.current_lab}`, p])).values(),
      );
      const res = await api.post(
        `/lab-analysis/${encodeURIComponent(laboratorio)}/transfer/preview/export`,
        { gh_current_lab_pairs: uniqPairs, disp_id: data?.disp_id || "" },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `traslado_${laboratorio.replace(/[^A-Za-z0-9_-]+/g, "_").slice(0, 40)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar el Excel");
    }
  };

  const applyChange = async () => {
    const cns = Array.from(selectedCns);
    if (!cns.length) {
      toast.error("Selecciona al menos un CN a trasladar");
      return;
    }
    setApplying(true);
    try {
      const { data: r } = await api.post(
        `/lab-analysis/${encodeURIComponent(laboratorio)}/transfer/apply`,
        { cns },
      );
      toast.success(`${r.updated} CN(s) trasladados a ${laboratorio}. Puedes retornar al modelo anterior si quieres.`);
      closePreview();
      await loadSnapshot();
      await loadAnalysis();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo aplicar el traslado");
    } finally {
      setApplying(false);
    }
  };

  const revertChange = async () => {
    if (!snapshot?.exists) return;
    if (!window.confirm(`¿Retornar al modelo anterior? Se restaurarán ${snapshot.n_entries} CN(s) a su laboratorio previo.`)) {
      return;
    }
    setReverting(true);
    try {
      const { data: r } = await api.post(
        `/lab-analysis/${encodeURIComponent(laboratorio)}/transfer/revert`,
      );
      toast.success(`${r.reverted} CN(s) restaurados al modelo anterior.`);
      setSnapshot(null);
      await loadAnalysis();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo revertir");
    } finally {
      setReverting(false);
    }
  };

  if (loading)
    return (
      <div className="p-6 text-center text-slate-500 text-sm bg-slate-50 rounded-lg border border-slate-200">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Cargando análisis…
      </div>
    );
  if (error)
    return (
      <div className="p-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg" data-testid="lab-analysis-error">
        {error}
      </div>
    );
  if (!data) return null;

  const t = data.totals || {};
  const agreementRows = onlyBuying
    ? (data.molecules_with_agreement || []).filter((m) => m.currently_buying)
    : (data.molecules_with_agreement || []);
  const losingRows = data.losing_money || [];

  return (
    <div className="space-y-3" data-testid="lab-analysis-panel">
      {/* Banner de snapshot activo (Retornar al modelo anterior) */}
      {snapshot?.exists && (
        <div
          className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-3"
          data-testid="lab-transfer-snapshot-banner"
        >
          <CheckCircle2 className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1 text-xs text-amber-900">
            <div className="font-semibold">Traslado aplicado · {snapshot.n_entries} CN(s) movidos a {laboratorio}</div>
            <div className="text-amber-800 mt-0.5">
              {snapshot.created_at ? `Aplicado el ${new Date(snapshot.created_at).toLocaleString("es-ES")}` : ""}
              . Si algo no cuadra, puedes retornar al modelo anterior en cualquier momento.
            </div>
          </div>
          {isAdmin && (
            <button
              onClick={revertChange}
              disabled={reverting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white border border-amber-400 text-amber-900 text-xs font-semibold hover:bg-amber-100 disabled:opacity-60"
              data-testid="lab-transfer-revert-btn"
            >
              {reverting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
              Retornar al modelo anterior
            </button>
          )}
        </div>
      )}

      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white">
        {/* Resumen */}
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200 border-b border-slate-200">
          <StatCell k="Condiciones cargadas" v={t.n_molecules_with_agreement || 0} />
          <StatCell k="Compradas ya" v={t.n_currently_buying || 0} cls="text-emerald-700" />
          <StatCell k="Perdiendo dinero" v={t.n_losing_money || 0} cls="text-rose-700" />
          <StatCell
            k="Ahorro potencial"
            v={`${(t.total_saving_eur || 0).toLocaleString("es-ES", { maximumFractionDigits: 0 })} €`}
            cls="text-slate-900 font-bold"
          />
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          <TabBtn active={tab === "agreement"} onClick={() => setTab("agreement")} testid="lab-tab-agreement">
            Condiciones por molécula ({data.molecules_with_agreement?.length || 0})
          </TabBtn>
          <TabBtn active={tab === "losing"} onClick={() => setTab("losing")} testid="lab-tab-losing">
            <TrendingDown className="h-3.5 w-3.5 inline mr-1 text-rose-500" />
            ¿Estás perdiendo dinero? ({losingRows.length})
          </TabBtn>
        </div>

        {tab === "agreement" ? (
          <AgreementTable rows={agreementRows} onlyBuying={onlyBuying} setOnlyBuying={setOnlyBuying} />
        ) : (
          <LosingTable
            rows={losingRows}
            laboratorio={laboratorio}
            totalSaving={t.total_saving_eur || 0}
            isAdmin={isAdmin}
            previewLoading={previewLoading}
            onPreviewOne={(r) => loadPreview([{ gh: r.gh, current_lab: r.current_lab }])}
            onPreviewAll={() =>
              loadPreview(losingRows.map((r) => ({ gh: r.gh, current_lab: r.current_lab })))
            }
          />
        )}
      </div>

      {/* Vista previa molécula-a-CN */}
      {preview && (
        <PreviewPanel
          preview={preview}
          selectedCns={selectedCns}
          setSelectedCns={setSelectedCns}
          onClose={closePreview}
          onExport={exportPreviewExcel}
          onApply={applyChange}
          applying={applying}
          canApply={isAdmin}
        />
      )}
    </div>
  );
}

function AgreementTable({ rows, onlyBuying, setOnlyBuying }) {
  return (
    <div>
      <div className="p-3 border-b border-slate-100 flex items-center gap-3 text-xs">
        <label className="inline-flex items-center gap-1.5 cursor-pointer">
          <input
            type="checkbox"
            checked={onlyBuying}
            onChange={(e) => setOnlyBuying(e.target.checked)}
            data-testid="lab-filter-buying"
          />
          Sólo las que ya compramos a este lab
        </label>
        <span className="text-slate-500 ml-auto">{rows.length} molécula(s)</span>
      </div>
      <div className="max-h-[400px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">Molécula</th>
              <th className="px-3 py-2 text-right">Descuento</th>
              <th className="px-3 py-2 text-right">Uds actuales</th>
              <th className="px-3 py-2 text-right">€ PVL actuales</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="lab-agreement-rows">
            {rows.map((m, i) => (
              <tr key={i} className={m.currently_buying ? "bg-emerald-50/30" : ""}>
                <td className="px-3 py-2 text-slate-800 truncate max-w-[380px]" title={m.gh}>
                  {m.gh}
                </td>
                <td className="px-3 py-2 text-right font-mono-num font-semibold">{m.discount_pct}%</td>
                <td className="px-3 py-2 text-right font-mono-num">
                  {m.currently_buying ? m.current_units_this_lab : "—"}
                </td>
                <td className="px-3 py-2 text-right font-mono-num">
                  {m.currently_buying
                    ? `${m.current_eur_this_lab.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €`
                    : "—"}
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-6 text-center text-slate-400 text-xs">
                  Sin resultados con el filtro actual.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LosingTable({ rows, laboratorio, totalSaving, isAdmin, previewLoading, onPreviewOne, onPreviewAll }) {
  return (
    <div>
      <div className="p-4 bg-rose-50 border-b border-rose-100 flex items-start gap-2">
        <AlertTriangle className="h-4 w-4 text-rose-600 mt-0.5 flex-shrink-0" />
        <div className="text-xs text-rose-900">
          Estás comprando <strong>{rows.length} molécula(s)</strong> a otro proveedor con MENOS descuento que el que te
          ofrece este laboratorio. Al trasladar ahorrarías{" "}
          <strong>{totalSaving.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €</strong> el mes.
        </div>
      </div>
      <div className="max-h-[400px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 text-left">Molécula</th>
              <th className="px-3 py-2 text-left">Compras ahora a</th>
              <th className="px-3 py-2 text-right">% Actual</th>
              <th className="px-3 py-2 text-right">% con {laboratorio}</th>
              <th className="px-3 py-2 text-right">Uds</th>
              <th className="px-3 py-2 text-right">Ahorro €</th>
              {isAdmin && <th className="px-3 py-2"></th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="lab-losing-rows">
            {rows.map((r, i) => (
              <tr key={i} className="hover:bg-slate-50">
                <td className="px-3 py-2 text-slate-800 truncate max-w-[280px]" title={r.gh}>
                  {r.gh}
                </td>
                <td className="px-3 py-2 text-slate-700 text-xs truncate max-w-[180px]" title={r.current_lab}>
                  {r.current_lab}
                </td>
                <td className="px-3 py-2 text-right font-mono-num text-rose-700">{r.current_pct}%</td>
                <td className="px-3 py-2 text-right font-mono-num text-emerald-700 font-semibold">
                  {r.this_lab_pct}%
                </td>
                <td className="px-3 py-2 text-right font-mono-num">{r.current_units}</td>
                <td className="px-3 py-2 text-right font-mono-num font-bold text-emerald-700">
                  +{r.potential_saving_eur.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
                </td>
                {isAdmin && (
                  <td className="px-3 py-2">
                    <button
                      onClick={() => onPreviewOne(r)}
                      disabled={previewLoading}
                      className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-slate-900 text-white hover:bg-slate-700 disabled:opacity-60"
                      data-testid={`lab-transfer-btn-${r.gh}`}
                    >
                      Cambiar <ArrowRight className="h-3 w-3" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={isAdmin ? 7 : 6} className="px-3 py-6 text-center text-slate-400 text-xs">
                  Ninguna molécula donde estés perdiendo dinero. Todo bajo control.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {isAdmin && rows.length > 0 && (
        <div className="p-3 border-t border-slate-200 bg-slate-50 flex justify-end">
          <button
            onClick={onPreviewAll}
            disabled={previewLoading}
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2 rounded-md disabled:opacity-60"
            data-testid="lab-transfer-preview-all"
          >
            {previewLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
            Ver traslado propuesto ({rows.length}) · Ahorro +
            {totalSaving.toLocaleString("es-ES", { maximumFractionDigits: 0 })} €
          </button>
        </div>
      )}
    </div>
  );
}

function PreviewPanel({ preview, selectedCns, setSelectedCns, onClose, onExport, onApply, applying, canApply }) {
  const rows = preview.rows || [];
  const totals = preview.totals || {};
  const allSelected = rows.length > 0 && selectedCns.size === rows.length;

  const toggleAll = () => {
    setSelectedCns(allSelected ? new Set() : new Set(rows.map((r) => r.cn)));
  };
  const toggleOne = (cn) => {
    const next = new Set(selectedCns);
    if (next.has(cn)) next.delete(cn);
    else next.add(cn);
    setSelectedCns(next);
  };

  const selectedRows = rows.filter((r) => selectedCns.has(r.cn));
  const selSaving = selectedRows.reduce((s, r) => s + (r.saving_eur || 0), 0);
  const selUnits = selectedRows.reduce((s, r) => s + (r.units || 0), 0);

  return (
    <div
      className="border-2 border-emerald-500 rounded-xl bg-white shadow-sm overflow-hidden"
      data-testid="lab-transfer-preview"
    >
      <div className="p-4 bg-emerald-50 border-b border-emerald-100 flex items-start gap-3">
        <ArrowRight className="h-5 w-5 text-emerald-700 mt-0.5" />
        <div className="flex-1 text-sm">
          <div className="font-bold text-emerald-900">Vista previa · Traslado a {preview.target_lab}</div>
          <div className="text-emerald-800 text-xs mt-0.5">
            Revisa molécula por molécula antes de aplicar. Puedes descartar las que no quieras trasladar.
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded hover:bg-emerald-100 text-emerald-700"
          data-testid="lab-transfer-preview-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-slate-200 border-b border-slate-200">
        <StatCell k="Líneas totales" v={totals.n_rows || 0} />
        <StatCell k="Con movimiento" v={totals.n_with_movement || 0} cls="text-emerald-700" />
        <StatCell k="Uds seleccionadas" v={selUnits.toLocaleString("es-ES")} />
        <StatCell
          k="Ahorro selección"
          v={`${selSaving.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €`}
          cls="text-emerald-700 font-bold"
        />
      </div>

      <div className="max-h-[480px] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
            <tr>
              <th className="px-2 py-2 w-8">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleAll}
                  data-testid="lab-transfer-select-all"
                />
              </th>
              <th className="px-3 py-2 text-left">CN</th>
              <th className="px-3 py-2 text-left">Nombre</th>
              <th className="px-3 py-2 text-left">Molécula</th>
              <th className="px-3 py-2 text-left">Lab actual → Destino</th>
              <th className="px-3 py-2 text-right">% Act → Dest</th>
              <th className="px-3 py-2 text-right">Uds</th>
              <th className="px-3 py-2 text-right">€ actuales</th>
              <th className="px-3 py-2 text-right">Ahorro €</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-testid="lab-transfer-preview-rows">
            {rows.map((r) => {
              const checked = selectedCns.has(r.cn);
              return (
                <tr key={r.cn} className={checked ? "bg-emerald-50/40" : "opacity-60"}>
                  <td className="px-2 py-2">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(r.cn)}
                      data-testid={`lab-transfer-check-${r.cn}`}
                    />
                  </td>
                  <td className="px-3 py-2 font-mono-num text-xs">{r.cn}</td>
                  <td className="px-3 py-2 text-slate-800 truncate max-w-[240px]" title={r.name}>
                    {r.name}
                  </td>
                  <td className="px-3 py-2 text-slate-700 text-xs truncate max-w-[200px]" title={r.gh}>
                    {r.gh}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span className="text-rose-700 truncate inline-block max-w-[110px] align-bottom" title={r.current_lab}>
                      {r.current_lab}
                    </span>
                    <ArrowRight className="h-3 w-3 inline mx-1 text-slate-400" />
                    <span className="text-emerald-700 font-semibold truncate inline-block max-w-[110px] align-bottom" title={r.target_lab}>
                      {r.target_lab}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num text-xs">
                    <span className="text-rose-700">{r.current_pct}%</span> →{" "}
                    <span className="text-emerald-700 font-semibold">{r.target_pct}%</span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num">{r.units}</td>
                  <td className="px-3 py-2 text-right font-mono-num">
                    {r.current_pvl_eur.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
                  </td>
                  <td className="px-3 py-2 text-right font-mono-num font-bold text-emerald-700">
                    +{r.saving_eur.toLocaleString("es-ES", { minimumFractionDigits: 2 })} €
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-6 text-center text-slate-400 text-xs">
                  No hay CNs candidatos con los criterios actuales.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="p-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
        <button
          onClick={onExport}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-white border border-slate-300 text-slate-800 text-xs font-semibold hover:bg-slate-100"
          data-testid="lab-transfer-export"
        >
          <FileDown className="h-3.5 w-3.5" /> Exportar Excel
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-md text-slate-600 text-xs hover:bg-slate-200"
            data-testid="lab-transfer-cancel"
          >
            Cancelar
          </button>
          {canApply && (
            <button
              onClick={onApply}
              disabled={applying || selectedCns.size === 0}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-4 py-2 rounded-md disabled:opacity-60"
              data-testid="lab-transfer-apply"
            >
              {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
              Aplicar cambio ({selectedCns.size})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCell({ k, v, cls = "text-slate-700" }) {
  return (
    <div className="p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</div>
      <div className={`mt-0.5 text-lg font-mono-num ${cls}`}>{v}</div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
        active ? "border-slate-950 text-slate-900 bg-white" : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}
